import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { PageLoader } from '../ui';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /**
   * Legacy Django-style staff flag. Kept for backwards compatibility but
   * no longer used by any caller — prefer `requireAdmin`. The API still
   * sends `is_staff: true` for ordinary teachers, so this is unsafe as an
   * authorisation gate.
   */
  requireStaff?: boolean;
  /**
   * Allows any admin: super_admin, org_admin, or principal. The API
   * derives `is_superuser` from `User.is_admin`, which is true for all
   * three. Use this for org-scoped admin pages (users management, admin
   * onboarding, analytics).
   */
  requireAdmin?: boolean;
  /**
   * Restricts to platform super-admins only — i.e. cross-tenant operators
   * who can see every school. Strictly stricter than `requireAdmin`. Use
   * this for platform-level admin views (org management, leads, etc).
   */
  requireSuperAdmin?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireStaff = false,
  requireAdmin = false,
  requireSuperAdmin = false,
}) => {
  const { isAuthenticated, isLoading, user } = useAuthStore();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return <PageLoader text="Checking authentication..." />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        state={{ from: location }}
        replace
      />
    );
  }

  // Platform super-admin gate (cross-tenant). The role list is the
  // authoritative source -- `is_superuser` is set for any admin tier so
  // it isn't strict enough on its own.
  if (requireSuperAdmin && !user?.roles?.includes('super_admin')) {
    return <Navigate to="/dashboard" replace />;
  }

  // Org-scoped admin gate. `is_superuser` mirrors `User.is_admin` on the
  // API side and covers principal / org_admin / super_admin.
  if (requireAdmin && !user?.is_superuser) {
    return <Navigate to="/dashboard" replace />;
  }

  // Legacy staff requirement. Kept for backwards compatibility but
  // prefer `requireAdmin` on new routes -- `is_staff` is true for plain
  // teachers, so this only blocks guardian-style accounts and is too
  // permissive for an admin gate.
  if (requireStaff && user && !user.is_staff) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            Access Denied
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            You don't have permission to access this page.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
